import moment, { Moment } from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { RecordFactory } from '../../models/RecordFactory'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import { PSLearnMore } from '../app/PSLearnMore'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import { Chip } from '../common/fields/Chip'
import { TextField } from '../common/fields/TextField'

export const SendTaxFormPage = () => {
    const user = PSApplication.me()
    const owner = PSApplication.owner()

    const navigate = useNavigate()
    const { logAndDisplayError } = useErrorHandler()

    const [selectedTaxYears, setSelectedTaxYears] = useState<string[]>([])
    const [isSending, setIsSending] = useState(false)
    const [showComplete, setShowComplete] = useState(false)
    const [learnMoreComponent, showLearnMore] = PSLearnMore([
        {
            key: 'email',
            title: 'Change Email',
            body: 'To send form to different email, please update email on file.',
        },
    ])

    const availableTaxYears = useMemo(() => {
        if (owner.firstPaymentDate === null) {
            return []
        }
        //Defaulting to the last 2 tax years for now
        return getAvailableTaxYears(2, owner.firstPaymentDate!)
    }, [owner.firstPaymentDate])

    const [alertMessage, setAlertMessage] = useState<string>(
        `Please check your email for your 1099K tax form.`
    )

    const onSend = useCallback(async () => {
        setIsSending(true)
        const result = await RecordFactory.getInstance().sendTaxForm(selectedTaxYears)
        setIsSending(false)

        if (!result.ok) {
            logAndDisplayError(result.error)
            return
        }
        else if (result.value) {
            setAlertMessage(result.value)
        }
        setShowComplete(true)
    }, [user?.type, selectedTaxYears])

    const openSuiteCenter = () =>
        window.open(
            'https://support.pocketsuite.io/hc/en-us/articles/360029087991-Will-I-get-a-1099-',
            '_blank'
        )

    return createPortal(
        <div className="fixed inset-0 flex h-screen w-full flex-col bg-gray-100">
            <div className={`flex-1 overflow-y-auto p-8`} data-testid="content-container">
                <Button
                    shape="square"
                    intent="tertiary_deprecated"
                    className="absolute top-[1.125rem] right-[2.125rem] z-10"
                    onClick={() => navigate(-1)}
                >
                    <Icon icon="close.large" />
                </Button>

                <main className="max-w-xlarge mx-auto w-full">
                    {isSending && <Spinner />}

                    {!isSending && (
                        <>
                            <Heading size="xl" className="my-12 text-center">
                                Tax form
                            </Heading>
                            <Card className="flex flex-col items-stretch justify-start gap-y-8">
                                <div className="flex items-start justify-start gap-x-4">
                                    <Icon
                                        icon="documentPen.large"
                                        background="round"
                                        className="shrink-0"
                                    />
                                    <div className="flex grow flex-col items-stretch justify-start gap-y-3">
                                        <div className="flex items-center justify-between gap-x-2">
                                            <Label className="block">Tax form</Label>
                                        </div>
                                        <div className="flex items-start justify-start gap-x-2">
                                            {availableTaxYears.map((year) => {
                                                return (
                                                    <Chip
                                                        key={year}
                                                        title={`${year} 1099-K`}
                                                        onClick={() => {
                                                            setSelectedTaxYears((prev) =>
                                                                prev.includes(year)
                                                                    ? prev.filter((y) => y !== year)
                                                                    : [...prev, year]
                                                            )
                                                        }}
                                                        isSelected={selectedTaxYears.includes(year)}
                                                    />
                                                )
                                            })}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-start justify-start gap-x-4">
                                    <Icon
                                        icon="envelope.large"
                                        background="round"
                                        className="shrink-0 opacity-50"
                                    />
                                    <div className="flex grow flex-col items-stretch justify-start gap-y-3 text-gray-400">
                                        <div className="flex items-center justify-between gap-x-2">
                                            <Label className="block">Send via email</Label>
                                        </div>
                                        <Label size="sm" className="text-secondary-text font-light">
                                            To send form to different email, please update email on
                                            file
                                        </Label>
                                        <TextField
                                            placeholder={owner.email}
                                            size="medium"
                                            className="w-full border-gray-300"
                                            disabled
                                        />
                                        <div className="flex items-start justify-start gap-x-2"></div>
                                    </div>
                                </div>
                            </Card>
                        </>
                    )}

                    {learnMoreComponent}

                    {showComplete && (
                        <Modal
                            isOpen={showComplete}
                            onClose={() => navigate('/settings')}
                            size="small"
                            title="Tax Form"
                            showDividers={false}
                            footer={<Button onClick={() => navigate('/settings')}>Ok</Button>}
                        >
                            {alertMessage}
                        </Modal>
                    )}
                </main>
            </div>

            <footer className="z-10 flex w-full items-center justify-end bg-white px-8 py-4 shadow-[0px_-1px_2px_0px_rgba(0,0,0,0.1)]">
                <Button
                    intent="secondary"
                    style={{
                        marginRight: '20px',
                    }}
                    onClick={() => openSuiteCenter()}
                >
                    Learn more
                </Button>

                <Button intent="primary" onClick={() => onSend()}>
                    Send
                </Button>
            </footer>
        </div>,
        document.body
    )
}

const getAvailableTaxYears = (numberOfYears: number, firstPaymentDate: Moment): string[] => {
    if (firstPaymentDate!.year() === moment().year()) {
        return []
    }

    const taxYears: string[] = []
    const today = new Date()
    let maxYear = today.getFullYear()
    const month = today.getMonth() // 0-based month
    const day = today.getDate()

    // Last year's tax forms become available on Jan 19th
    if (month === 0 && day < 19) {
        maxYear -= 2
    }
    else {
        maxYear -= 1
    }

    const firstPaymentYear = firstPaymentDate.year()
    const minYear = Math.max(firstPaymentYear, maxYear - numberOfYears + 1)
    for (let year = maxYear; year >= minYear; year--) {
        taxYears.push(year.toString())
    }

    return taxYears
}
